import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import usuarioService from "../services/usuarioService.ts";
import { CatalogoPermissoes, TipoUsuario, UsuarioLogado } from "../types/Permissoes.ts";

type PermissoesContextType = {
  usuario: UsuarioLogado | null;
  tipo: TipoUsuario | null;
  isAdm: boolean;
  podeExcluirAgendamento: boolean;
  podeVerLog: boolean;
  catalogo: CatalogoPermissoes | null;
  isLoading: boolean;
  /** Nao foi possivel confirmar a funcao; a interface esta liberando por padrao. */
  erroAoCarregar: boolean;
  refresh: () => Promise<void>;
};

const PermissoesContext = createContext<PermissoesContextType | undefined>(undefined);

/**
 * Cache em escopo de modulo, mesmo molde do usePlanLimits - que existe
 * exatamente por este motivo.
 *
 * Sem ele, cada navegacao refazia GET /usuario/me e GET /usuario/funcionalidades.
 * Carregar Configuracoes ja dispara mais de vinte requisicoes, e o pool Hikari
 * do backend e de 3 conexoes: duas chamadas a mais por tela empurram todo mundo
 * para a fila e a tela demora visivelmente para preencher.
 *
 * A chave e o token: trocar de conta invalida sozinho, sem precisar lembrar de
 * limpar nada no logout.
 */
let cache: { token: string; usuario: UsuarioLogado | null; catalogo: CatalogoPermissoes | null } | null = null;
let inflight: Promise<void> | null = null;

/** Chamar quando a funcao do proprio usuario puder ter mudado. */
export const invalidarCachePermissoes = () => {
  cache = null;
};

export const PermissoesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuario, setUsuario] = useState<UsuarioLogado | null>(null);
  const [catalogo, setCatalogo] = useState<CatalogoPermissoes | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Nao deu para saber quem e o usuario. Enquanto isto for verdade a interface
   * nao esconde nada - ver o comentario em `valor`, abaixo.
   */
  const [erroAoCarregar, setErroAoCarregar] = useState(false);

  const carregar = useCallback(async (forcar = false) => {
    const token = localStorage.getItem("token");
    if (!token) {
      cache = null;
      setUsuario(null);
      setCatalogo(null);
      setErroAoCarregar(false);
      setIsLoading(false);
      return;
    }

    if (forcar) cache = null;

    if (cache && cache.token === token) {
      setUsuario(cache.usuario);
      setCatalogo(cache.catalogo);
      setErroAoCarregar(!cache.usuario);
      setIsLoading(false);
      return;
    }

    // Duas abas do app montando ao mesmo tempo nao podem virar duas idas ao
    // servidor - a segunda espera a primeira, como no usePlanLimits.
    if (inflight) {
      await inflight;
      if (cache && cache.token === token) {
        setUsuario(cache.usuario);
        setCatalogo(cache.catalogo);
        setErroAoCarregar(!cache.usuario);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);

    // As duas juntas: o catalogo e o mesmo para todo mundo e nao muda em tempo
    // de execucao, entao nao vale uma segunda ida so para ele.
    const buscar = () => Promise.all([
      usuarioService.meusDados(),
      usuarioService.catalogoPermissoes(),
    ]);

    inflight = (async () => {
      try {
        let meus: UsuarioLogado | null;
        let cat: CatalogoPermissoes | null;
        try {
          [meus, cat] = await buscar();
        } catch {
          // Uma segunda chance antes de desistir: sem isso, um soluco de rede
          // no carregamento da pagina deixaria a sessao em modo degradado.
          [meus, cat] = await buscar();
        }
        // Falha nao entra no cache: a proxima tela tenta de novo, em vez de
        // ficar a sessao inteira degradada por causa de um soluco.
        if (meus) cache = { token, usuario: meus, catalogo: cat };
        setUsuario(meus);
        setCatalogo(cat);
        setErroAoCarregar(!meus);
      } catch {
        setUsuario(null);
        setCatalogo(null);
        setErroAoCarregar(true);
      } finally {
        setIsLoading(false);
        inflight = null;
      }
    })();

    await inflight;
  }, []);

  /**
   * Recarrega quando o token muda - e nao so na montagem.
   *
   * O provider vive acima das telas e nunca remonta durante a sessao. Como login
   * e logout apenas gravam/apagam o token no localStorage e chamam navigate()
   * (sem reload), carregar uma unica vez deixava a sessao inteira com os dados de
   * quem estava logado antes: entrar com outra conta na mesma aba mostrava o nome
   * e a funcao do usuario anterior ate alguem dar F5. Era isso que exibia
   * "Administrador" para um Instrumentador e liberava abas e botoes que o
   * servidor depois negava com 403.
   *
   * O useLocation faz o provider re-renderizar a cada navegacao, e toda troca de
   * sessao termina num navigate() - por isso o sinal e confiavel e cobre os dois
   * caminhos de login e os tres de logout, sem precisar tocar em nenhum deles.
   * O token como dependencia garante que so recarrega quando ele realmente muda.
   */
  useLocation();
  const tokenAtual = localStorage.getItem("token");

  useEffect(() => {
    carregar();
  }, [carregar, tokenAtual]);

  const valor = useMemo<PermissoesContextType>(() => {
    // Quando nao foi possivel descobrir a funcao, a interface NAO esconde nada.
    //
    // Esconder parecia a escolha segura, e e o oposto: quem autoriza de verdade
    // e o FuncionalidadeInterceptor no servidor, entao o pior caso aqui e o
    // usuario clicar em algo e receber um 403 com mensagem clara. Falhando
    // fechado, o pior caso era o dono da conta perder o acesso a propria
    // assinatura, calado, por causa de um soluco de rede - foi o que aconteceu
    // rodando o frontend novo contra um backend que ainda nao tinha /usuario/me.
    if (erroAoCarregar) {
      return {
        usuario: null,
        tipo: null,
        isAdm: true,
        podeExcluirAgendamento: false, // acao destrutiva: esta continua fechada
        podeVerLog: true,
        catalogo,
        isLoading,
        erroAoCarregar: true,
        refresh: () => carregar(true),
      };
    }

    return {
      usuario,
      tipo: usuario?.tipo ?? null,
      isAdm: usuario?.tipo === "ADM",
      podeExcluirAgendamento: !!usuario?.podeExcluirAgendamento,
      podeVerLog: !!usuario?.podeVerLog,
      catalogo,
      isLoading,
      erroAoCarregar: false,
      refresh: () => carregar(true),
    };
  }, [usuario, catalogo, isLoading, erroAoCarregar, carregar]);

  return <PermissoesContext.Provider value={valor}>{children}</PermissoesContext.Provider>;
};

export const usePermissoes = (): PermissoesContextType => {
  const ctx = useContext(PermissoesContext);
  if (!ctx) throw new Error("usePermissoes precisa estar dentro de PermissoesProvider");
  return ctx;
};
